import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { API_URL } from '@/lib/api';
import { formatBytes, formatTimestamp } from '@/lib/drive/format';
import { appConfirm } from '@/lib/dialogs';

interface ManagedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
  quota: { used: string; hard: string };
}

/**
 * The admin console's user management: roles, suspension and per-user quota.
 * Every action rides the hub's `/api/vault/api/admin/users` surface and is
 * guarded there by the ADMIN role — this page only ever draws what it answers.
 */
export function ManageUsersPage() {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState<ManagedUser[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_URL}/api/admin/users`, {
      headers: { Authorization: `Bearer ${token ?? ''}` },
      cache: 'no-store',
    });
    if (!res.ok) {
      toast.error(t('admin.manageLoadFailed'));
      setUsers([]);
      return;
    }
    const data = (await res.json()) as { users: ManagedUser[] };
    setUsers(data.users ?? []);
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = async (userId: string, body: Record<string, unknown>) => {
    setBusyId(userId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error || `Request failed with ${res.status}`);
      }
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const setQuota = async (userId: string, gigabytes: string) => {
    const value = Number(gigabytes);
    if (!Number.isFinite(value) || value < 0) return;
    setBusyId(userId);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/quota`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
        body: JSON.stringify({ hardBytes: value > 0 ? Math.round(value * 1024 ** 3) : 0 }),
      });
      if (!res.ok) throw new Error(`Request failed with ${res.status}`);
      toast.success(t('admin.quotaSaved'));
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const toggleSuspension = async (user: ManagedUser) => {
    const suspending = user.status !== 'SUSPENDED';
    if (suspending) {
      const confirmed = await appConfirm(t('admin.suspendWarning', { name: user.name }), {
        title: t('admin.suspend'),
        confirmLabel: t('admin.suspend'),
        cancelLabel: t('common.cancel'),
        variant: 'error',
      });
      if (!confirmed) return;
    }
    await patch(user.id, { status: suspending ? 'SUSPENDED' : 'ACTIVE' });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4" data-tour-id="admin-manage">
      <div className="flex flex-nowrap items-center justify-between gap-3">
        <h1 className="flex min-w-0 items-center gap-2 truncate whitespace-nowrap text-xl font-semibold text-foreground">
          <UserPlusIcon className="h-5 w-5 flex-shrink-0 text-accent" />
          {t('admin.manageTitle')}
        </h1>
      </div>

      <div className="rounded-lg border border-border-color bg-card">
        {users === null ? (
          <div className="flex items-center justify-center p-10">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-color border-t-accent" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border-color text-xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="px-3 py-2 text-start font-medium">{t('admin.user')}</th>
                  <th scope="col" className="w-40 px-3 py-2 text-start font-medium">{t('admin.role')}</th>
                  <th scope="col" className="w-40 px-3 py-2 text-start font-medium">{t('admin.quotaGib')}</th>
                  <th scope="col" className="w-40 px-3 py-2 text-start font-medium">{t('admin.status')}</th>
                  <th scope="col" className="w-36 px-3 py-2 text-start font-medium">{t('admin.joined')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className={clsx(
                      'border-b border-border-color/50 last:border-b-0',
                      busyId === user.id && 'opacity-50',
                    )}
                  >
                    <td className="min-w-0 px-3 py-2">
                      <span className="block truncate whitespace-nowrap text-foreground">{user.name}</span>
                      <span className="block truncate whitespace-nowrap text-xs text-muted-foreground">{user.email}</span>
                      <span className="block whitespace-nowrap text-xs text-muted-foreground">
                        {formatBytes(Number(user.quota.used))} / {formatBytes(Number(user.quota.hard))}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={user.role}
                        onChange={(event) => void patch(user.id, { role: event.target.value })}
                        className="w-full whitespace-nowrap rounded-md border border-border-color bg-background px-2 py-1 text-xs text-foreground"
                      >
                        <option value="MEMBER">{t('admin.roleMember')}</option>
                        <option value="MODERATOR">{t('admin.roleModerator')}</option>
                        <option value="ADMIN">{t('admin.roleAdmin')}</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        placeholder={String(Math.round(Number(user.quota.hard) / 1024 ** 3)) || '—'}
                        onBlur={(event) => {
                          if (event.target.value !== '') void setQuota(user.id, event.target.value);
                          event.target.value = '';
                        }}
                        className="w-24 rounded-md border border-border-color bg-background px-2 py-1 text-xs text-foreground"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => void toggleSuspension(user)}
                        className={clsx(
                          'whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium transition-colors',
                          user.status === 'SUSPENDED'
                            ? 'bg-muted text-muted-foreground hover:text-foreground'
                            : 'bg-success/10 text-success hover:bg-success/20',
                        )}
                      >
                        {user.status === 'SUSPENDED' ? t('admin.suspended') : t('admin.active')}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {formatTimestamp(new Date(user.createdAt).getTime(), i18n.resolvedLanguage)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
